import { supabase } from "@cl/supabase";
import {
  LEAD_STATUSES,
  type LeadLanguage,
  type LeadStatus,
} from "@u/leadStatus";
import { demoRequestSchema } from "@u/leadValidation";

export type DemoProduct = "360";
/** What the visitor wants to book: a product demo or just a conversation. */
export type DemoRequestKind = "demo" | "call" | "quote";

/** Public booking context for the /book form: whose agenda + tz to query. */
export interface AdminBookingContext {
  ownerId: string;
  timezone: string;
  hasAvailability: boolean;
}

/** Public — resolve the admin's agenda so the /book form can offer real slots.
 * Returns null when no admin/settings exist (form falls back to a free date). */
export async function fetchAdminBookingContext(): Promise<AdminBookingContext | null> {
  const { data: adminId, error } = await supabase.rpc("get_admin_owner_id");
  if (error || !adminId) return null;
  const { data } = await supabase
    .from("v_public_booking_settings")
    .select("timezone, has_availability")
    .eq("user_id", adminId as string)
    .is("venue_id", null)
    .maybeSingle();
  return {
    ownerId: adminId as string,
    timezone: data?.timezone ?? "Europe/Lisbon",
    hasAvailability: !!data?.has_availability,
  };
}

export interface DemoRequest {
  id: string;
  kind: DemoRequestKind;
  name: string | null;
  email: string;
  phone: string | null;
  company: string | null;
  product: DemoProduct;
  preferred_date: string | null;
  preferred_at: string | null;
  message: string | null;
  locale: string | null;
  preferred_language: LeadLanguage | null;
  source: string | null;
  status: LeadStatus;
  notes: string | null;
  created_at: string;
}

export interface InsertDemoRequestPayload {
  email: string;
  kind?: DemoRequestKind;
  name?: string;
  phone?: string;
  company?: string;
  product?: DemoProduct;
  preferred_date?: string | null;
  /** Exact slot picked against the admin's booking availability. */
  preferred_at?: string | null;
  message?: string;
  locale?: string;
  preferred_language?: LeadLanguage;
  source?: string;
}

/** Public — submit a demo request via the abuse-protected gateway (Turnstile +
 * rate limit + service-role insert). Anon INSERT on demo_requests is revoked. */
export async function insertDemoRequest(
  payload: InsertDemoRequestPayload,
  captchaToken: string,
): Promise<void> {
  const clean = demoRequestSchema.parse(payload);
  const { error } = await supabase.functions.invoke("submit-public-form", {
    body: { form: "demo_request", payload: clean, captchaToken },
  });
  if (error) throw error;
}

export { LEAD_STATUSES };
export type { LeadStatus };
